import { Text } from '@blocksuite/affine/store';

import type { DocsService } from '../doc';
import {
  CABINET_SECTIONS,
  type CabinetSectionKey,
  DEFAULT_CABINET_SECTIONS,
} from './sections';

export interface CreateCabinetOptions {
  title?: string;
  sections?: CabinetSectionKey[];
  /** Public link to a shared database view (the budget). */
  budgetViewUrl?: string;
}

const DEFAULT_TITLE = 'Личный кабинет пары';

/**
 * Creates the client cabinet as an ordinary OrgHub doc.
 *
 * The document is the unit of sharing, so a cabinet is just a doc — but a doc
 * with a predictable skeleton: one `h2` per selected section and either a text
 * placeholder or the embedded budget view underneath. Unchecked sections never
 * appear, which is the "галочками" flow from the meeting.
 *
 * Blocks are added after `createDoc` has returned (not inside `onStoreLoad`):
 * building the whole doc inside the store-load callback mounts the doc list in
 * the same React transition and aborts it ("transition was aborted" overlay).
 */
export async function createCabinetDoc(
  docsService: DocsService,
  options: CreateCabinetOptions = {}
): Promise<string> {
  const selected = new Set(
    options.sections && options.sections.length
      ? options.sections
      : DEFAULT_CABINET_SECTIONS
  );
  const budgetViewUrl = options.budgetViewUrl?.trim();

  const docRecord = docsService.createDoc({
    title: options.title?.trim() || DEFAULT_TITLE,
  });

  const { doc, release } = docsService.open(docRecord.id);
  try {
    // Wait for the new doc's store before writing: writing into a store that
    // has not finished loading leaves the doc in a "hidden" state and aborts
    // the React transition that opened it.
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    const note = store.getBlocksByFlavour('affine:note').at(0);
    if (!note) return docRecord.id;

    const noteId = note.id;
    store.transact(() => {
      for (const section of CABINET_SECTIONS) {
        if (!selected.has(section.key)) continue;

        store.addBlock(
          'affine:paragraph',
          { type: 'h2', text: new Text(section.title) },
          noteId
        );

        if (section.needsViewLink && budgetViewUrl) {
          store.addBlock(
            'affine:embed-iframe',
            {
              url: budgetViewUrl,
              iframeUrl: budgetViewUrl,
              title: section.title,
            },
            noteId
          );
          continue;
        }

        store.addBlock(
          'affine:paragraph',
          {
            text: new Text(
              section.needsViewLink && !budgetViewUrl
                ? 'Вставьте публичную ссылку на вид сметы — она появится здесь.'
                : section.placeholder
            ),
          },
          noteId
        );
      }
    });
  } finally {
    release();
  }

  return docRecord.id;
}
